// @flow

import { SURVEY_LAYER_VALUES, SURVEY_LAYERS } from 'js/constants/SurveyLayers';
import UUID from 'js/algorithms/UUID';
import {
  ClassificationInterval,
  getSatelliteConfig,
  Interval,
  PrescriptionJob,
  MetaTypes,
  isSoilLayer,
} from './PrescriptionJob';
import {
  PRESCRIPTION_OVERRIDE_COLORS,
  PRESCRIPTION_UNIT,
} from 'js/constants/PrescriptionConstants';
import { getMax, getMin } from '../../helpers/NumberUtils';
import { getSurveyConfig } from '../../model/surveys/SurveyLayerConfigFactory';
import * as turf from '@turf/turf';
import type { FieldLayer } from '../../constants/FieldLayers';
import { iterateRawValues, iterateValidValues } from '../../helpers/SurveyUtils';
import { getOtherLayerConfig, OTHER_LAYERS } from '../../constants/OtherLayers';
import { SATELLITE_LAYERS } from '../../constants/SatelliteLayers';
import { getYieldLayerConfig, YIELD_LAYERS } from '../../constants/YieldLayer';
import { mean, matrix, map, subset } from 'mathjs';
import { isDemandLayerType } from '../../model/surveys/country/SurveyCountryStrategy';
import async from 'resolve';
import { getNullIntervalLayout, isNullInterval, NULL_INTERVAL_VALUE } from './nullValueRegions';

const tinycolor = require('tinycolor2');

export const DEFAULT_INTERVALS_COUNT = 4;
export const DEFAULT_MAX_PRESCRIPTION = 150;
export const SQUARE_METER_COVERSION_VALUE = 10000;

export function initializeOverrides(prescriptionJob: PrescriptionJob) {
  let existing;

  if (Array.isArray(prescriptionJob.overrides) && Array.isArray(prescriptionJob.overrides[0])) {
    existing = prescriptionJob.overrides;
  }

  const values = prescriptionJob.values;
  const height = values.length;
  const width = values[0].length;

  if (existing) {
    // If the overrides array exists we must pad the array with missing -1's, since it's stored as a SparseArray in the DB
    // That means, that all -1's after the last valid override value is pruned on the backend.
    // We need to put them back to ensure the dimensions of Overrides and Values match.

    for (let y = 0; y < height; y++) {
      const row = existing[y];

      if (row) {
        // If the row exists, we need to find out if the dimensions match
        for (let x = 0; x < width; x++) {
          const diff = width - row.length;

          if (diff > 0) {
            // The dimensions do not match, so we pad with -1's
            for (let d = 0; d < diff; d++) {
              row.push(-1);
            }
          }
        }
      } else {
        // push an entire new row of -1's
        existing.push(Array(width).fill(-1));
      }
    }
  } else {
    return Array(height).fill(Array(width).fill(-1));
  }

  return existing;
}

const countOverriddenNullCells = (values, overrides) =>
  values.reduce(
    (count, row, y) =>
      count +
      row.reduce(
        (rowCount, value, x) =>
          rowCount + (value === null && overrides[y] && overrides[y][x] >= 0 ? 1 : 0),
        0
      ),
    0
  );

export function calculateOverridesArea(prescriptionJob) {
  const { overrides, fieldSize, values, overrideAreas, layerType } = prescriptionJob;

  // This is the result object.
  const newAreas = { ...overrideAreas };

  // Calculate the area (hectares) per pixel.
  const nullOverrideCount = countOverriddenNullCells(values, overrides);
  const pixelCount = isDemandLayerType(layerType)
    ? values.flat().filter((v) => v !== null).length
    : values
        .flat()
        .filter(
          (v) =>
            v > 0 || (v === NULL_INTERVAL_VALUE && prescriptionJob.intervals.some(isNullInterval))
        ).length;
  const totalPixelCount = pixelCount + nullOverrideCount;
  const areaPerPixel = fieldSize / totalPixelCount;

  // Flatten and separate exclusions from overrides and exclude non-value (-1) pixels from both.
  const flattened = overrides.flat();
  const flatOverrides = flattened.filter((val) => val > 0);
  const flatExclusions = flattened.filter((val) => val === 0);

  // Define the exclusion area as black for all values equal to 0.
  const exclusionArea = {
    area: flatExclusions.length * areaPerPixel,
    color: '#000',
    value: 0,
  };

  // Reset the areas counts of the existing overridden areas.
  Object.keys(newAreas).forEach((val) => {
    newAreas[val].area = 0;
  });

  // Overwrite the exclusion area
  newAreas[0] = exclusionArea;

  // Iterate all overridden values and either append the areaPerPixel to the existing area og start a new area.
  // When appending to a new area, we reuse the already set color to avoid areas changing color.
  // When starting a new area, we look for the next valid (not used) color in the default colors.
  flatOverrides.forEach((val) => {
    const existing = newAreas[val];
    if (existing) {
      existing.area += areaPerPixel;
    } else {
      newAreas[val] = {
        area: areaPerPixel,
        color: getNextOverrideColor(newAreas),
        value: val,
      };
    }
  });

  // If any areas do not have an area above 0 it means it has been deleted using the eraser, overridden with another value, or deleted from the list.
  // Thus, we delete it.
  Object.keys(newAreas).forEach((val) => {
    if (newAreas[val].area === 0) {
      delete newAreas[val];
    }
  });

  // Return the new overrideAreas.
  return newAreas || {};
}

export function calculateIntervals(prescriptionJob: PrescriptionJob) {
  let intervalsCount = prescriptionJob.intervals
    ? prescriptionJob.intervals.length
    : DEFAULT_INTERVALS_COUNT;
  if (intervalsCount === 0) {
    intervalsCount = DEFAULT_INTERVALS_COUNT;
  }
  const flatValues = [].concat(...prescriptionJob.values).filter((val) => val !== null && val > 0);

  let valuesMin, valuesMax;

  valuesMax = getMax(flatValues);
  valuesMin = getMin(flatValues);

  const span = valuesMax - valuesMin;

  let intervalWidth = 0;
  if (span === 0) {
    intervalsCount = 1;
  } else {
    intervalWidth = span / intervalsCount;
  }

  const intervals = [];

  for (let i = 0; i < intervalsCount; i++) {
    let min = Math.max(0, valuesMin);

    if (i > 0) {
      min = intervals[i - 1].max;
    }

    const max = Math.min(min + intervalWidth, valuesMax);

    let prescription;
    if (prescriptionJob.metaType === 'SPOT_SPRAYING') {
      prescription = 0;
    } else {
      if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
        prescription =
          prescriptionJob.maxPrescription /
          (prescriptionJob.fieldSize * SQUARE_METER_COVERSION_VALUE);
      } else {
        prescription = prescriptionJob.maxPrescription / prescriptionJob.fieldSize;
      }
    }

    intervals.push(new Interval(min, max, prescription));
  }

  return intervals;
}

export function calculateBulkIntervals(
  prescriptionJobs: Record<number, PrescriptionJob>,
  firstJobFieldId: number
) {
  let intervalsCount = DEFAULT_INTERVALS_COUNT;

  const flatValues = Object.values(prescriptionJobs)
    .flatMap((job) => job.values)
    .flat()
    .filter((val) => val !== null && val > 0);

  const valuesMin = getMin(flatValues);
  const valuesMax = getMax(flatValues);

  const span = valuesMax - valuesMin;

  let intervalWidth;
  if (span === 0) {
    intervalsCount = 1;
  } else {
    intervalWidth = span / intervalsCount;
  }

  const firstJob = prescriptionJobs[firstJobFieldId];
  const intervals = [];

  for (let i = 0; i < intervalsCount; i++) {
    let min = Math.max(0, valuesMin);

    if (i > 0) {
      min = intervals[i - 1].max;
    }

    const max = Math.min(min + intervalWidth, valuesMax);

    let prescription;
    if (firstJob.metaType === 'SPOT_SPRAYING') {
      prescription = 0;
    } else {
      if (firstJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
        prescription =
          firstJob.maxPrescription / (firstJob.fieldSize * SQUARE_METER_COVERSION_VALUE);
      } else {
        prescription = firstJob.maxPrescription / firstJob.fieldSize;
      }
    }

    intervals.push(new Interval(min, max, prescription));
  }

  return intervals;
}

export function calculateBulkSoilIntervals(
  prescriptionJobs: Record<number, PrescriptionJob>,
  firstJobFieldId: number
) {
  const flatValues = Object.values(prescriptionJobs)
    .flatMap((job) => job.values)
    .flat()
    .filter((val) => val !== null && val > 0);

  const valuesMin = getMin(flatValues);

  // Number of intervals should be equal to the number of unique soil types across all fields
  const uniqueValues = Array.from(new Set(flatValues)).sort((a, b) => a - b);
  const intervalsCount = uniqueValues.length;

  const firstJob = prescriptionJobs[firstJobFieldId];
  const intervals = [];

  for (let i = 0; i < intervalsCount; i++) {
    let min = Math.max(0, valuesMin);

    if (i > 0) {
      min = uniqueValues[i];
    }

    const max = min + 1;

    let prescription;
    if (firstJob.metaType === 'SPOT_SPRAYING') {
      prescription = 0;
    } else {
      if (firstJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
        prescription =
          firstJob.maxPrescription / (firstJob.fieldSize * SQUARE_METER_COVERSION_VALUE);
      } else {
        prescription = firstJob.maxPrescription / firstJob.fieldSize;
      }
    }

    intervals.push(new Interval(min, max, prescription));
  }

  return intervals;
}

/*
 * Util function to apply the percentage-based intervals (used for bulk prescriptions) to the next prescription job, by calculating the actual min and max values based on job values.
 */
export function applyBulkIntervals(
  prescriptionJob: PrescriptionJob,
  templateIntervals: Interval[]
) {
  const flatValues = [].concat(...prescriptionJob.values).filter((val) => val !== null && val > 0);

  const valuesMin = getMin(flatValues);
  const valuesMax = getMax(flatValues);
  const newIntervals = templateIntervals.reduce((acc, interval) => {
    if (isNullInterval(interval)) {
      if (prescriptionJob.values.some((row) => row.includes(NULL_INTERVAL_VALUE))) {
        acc.push(new Interval(NULL_INTERVAL_VALUE, NULL_INTERVAL_VALUE, interval.prescription));
      }
      return acc;
    }
    const min = Math.max(interval.min, valuesMin);
    const max = Math.min(interval.max, valuesMax);

    if (max > min) {
      acc.push(new Interval(min, max, interval.prescription));
    }

    return acc;
  }, []);

  return newIntervals;
}

/*
 * Util function to apply the soil type intervals (used for bulk soil prescriptions) to the next prescription job.
 */
export function applyBulkSoilIntervals(
  prescriptionJob: PrescriptionJob,
  templateIntervals: Interval[]
) {
  const flatValues = [].concat(...prescriptionJob.values).filter((val) => val !== null && val > 0);

  const fieldSoilTypes = new Set(flatValues);

  const newIntervals = templateIntervals.reduce((acc, interval) => {
    if (fieldSoilTypes.has(interval.min)) {
      acc.push(new Interval(interval.min, interval.max, interval.prescription));
    }

    return acc;
  }, []);

  return newIntervals;
}

/*
 * Util functions relevant for soil type layers, where each integer value represents a different soil type.
 */
export function calculateSoilIntervals(prescriptionJob: PrescriptionJob) {
  const flatValues = [].concat(...prescriptionJob.values).filter((val) => val !== null && val > 0);

  let valuesMin, valuesMax;

  valuesMax = getMax(flatValues) + 1;
  valuesMin = getMin(flatValues);

  // Number of intervals should be equal to the number of unique values (soil types) in the array
  const uniqueValues = Array.from(new Set(flatValues)).sort((a, b) => a - b);
  const span = new Set(flatValues).size;

  let intervalsCount = prescriptionJob.intervals ? prescriptionJob.intervals.length : span;
  if (intervalsCount === 0) {
    intervalsCount = span;
  }

  const intervals = [];

  for (let i = 0; i < intervalsCount; i++) {
    let min = Math.max(0, valuesMin);

    if (i > 0) {
      min = uniqueValues[i];
    }

    const max = min + 1;

    let prescription;
    if (prescriptionJob.metaType === 'SPOT_SPRAYING') {
      prescription = 0;
    } else {
      if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
        prescription =
          prescriptionJob.maxPrescription /
          (prescriptionJob.fieldSize * SQUARE_METER_COVERSION_VALUE);
      } else {
        prescription = prescriptionJob.maxPrescription / prescriptionJob.fieldSize;
      }
    }

    intervals.push(new Interval(min, max, prescription));
  }

  return intervals;
}

export function getAllUniqueValues(values) {
  const allValues = values.flat().filter((v) => v !== null);

  return Array.from(new Set(allValues)).sort((a, b) => a - b);
}

/**
 * Util function for getting values to use for soil layer.
 * In bulk mode (before initial intervals are applied) merge values from all bulk jobs
 * so that soil categories across all fields are included.
 * Else, returns the single field's values as is.
 */
export function getEffectiveSoilValues(values, layer, isBulkMode, bulkPrescriptionJobs) {
  if (!isSoilLayer(layer) || !isBulkMode || !bulkPrescriptionJobs) {
    return values;
  }

  // Collect all unique soil values across all bulk jobs
  const allUniqueTypes = new Set();
  Object.values(bulkPrescriptionJobs).forEach((job) => {
    if (job.values) {
      job.values.flat().forEach((v) => {
        if (v !== null && v > 0) {
          allUniqueTypes.add(v);
        }
      });
    }
  });

  return [Array.from(allUniqueTypes).sort((a, b) => a - b)];
}

export function getIntervalUniqueValues(interval, values) {
  const intervalValues = values
    .flat()
    .filter(
      (v) =>
        v !== null &&
        (isNullInterval(interval)
          ? v === NULL_INTERVAL_VALUE
          : v >= interval.min && v < interval.max)
    );
  const uniqueValues = Array.from(new Set(intervalValues)).sort((a, b) => a - b);

  return uniqueValues;
}

export function getSoilValuesCount(currInterval, prevIntervals, values) {
  // Get unique values for this interval
  const thisCount = getIntervalUniqueValues(currInterval, values).length;
  // Get total unique values across all intervals
  const totalCount = getAllUniqueValues(values).length;
  // Get unique values for all previous intervals
  const prevCount = prevIntervals.reduce((acc, i) => {
    return acc + getIntervalUniqueValues(i, values).length;
  }, 0);

  return { thisCount, prevCount, totalCount };
}

export function getIntervalDisplayBounds(intervals, index, soilValues) {
  const interval = intervals[index];
  const { nullWidth, normalWidth, normalMin } = getNullIntervalLayout(intervals);
  if (isNullInterval(interval)) return { left: 0, width: nullWidth };

  if (soilValues) {
    const { thisCount, prevCount, totalCount } = getSoilValuesCount(
      interval,
      intervals.slice(0, index),
      soilValues
    );
    const nullCount = nullWidth ? getIntervalUniqueValues(intervals[0], soilValues).length : 0;
    const normalCount = totalCount - nullCount;
    if (normalCount === 0) return { left: nullWidth, width: normalWidth };
    return {
      left: nullWidth + ((prevCount - nullCount) / normalCount) * normalWidth,
      width: (thisCount / normalCount) * normalWidth,
    };
  }

  const normalSpan = intervals[intervals.length - 1].max - normalMin;
  if (normalSpan === 0) return { left: nullWidth, width: normalWidth };
  return {
    left: nullWidth + ((interval.min - normalMin) / normalSpan) * normalWidth,
    width: ((interval.max - interval.min) / normalSpan) * normalWidth,
  };
}

export function calculateClassificationIntervals(prescriptionJob: PrescriptionJob) {
  if (!prescriptionJob.classifications) {
    return [];
  }
  const intervals = [];
  const fineValues = prescriptionJob.classifications['mappings']['FINE'];
  const values = prescriptionJob.classifications['values'];
  const flatValues = []
    .concat(...values)
    .filter((val, i, a) => val !== null && a.indexOf(val) === i)
    .sort((a, b) => {
      return a - b;
    });

  let prescription;
  if (prescriptionJob.metaType === 'SPOT_SPRAYING') {
    prescription = 0;
  } else {
    prescription = prescriptionJob.maxPrescription / prescriptionJob.fieldSize;
  }

  flatValues.forEach((number, idx) => {
    const classification = Object.keys(fineValues).find((key) => fineValues[key].includes(number));
    intervals.push(
      new ClassificationInterval(number - 0.5, number + 0.5, prescription, classification)
    );
  });
  return intervals;
}

export function calculateMergedIntervals(primaryIndex, secondaryIndex, option, intervalsInput) {
  let intervals = [...intervalsInput];
  let left, right;

  // Figure our the position of the intervals relative to each other
  if (primaryIndex < secondaryIndex) {
    left = primaryIndex;
    right = secondaryIndex;
  } else {
    left = secondaryIndex;
    right = primaryIndex;
  }

  const leftInterval = intervals[left];
  const rightInterval = intervals[right];

  let prescription;

  if (option === 0) {
    // Use left prescription
    prescription = leftInterval.prescription;
  } else if (option === 1) {
    // Average prescriptions
    prescription = (leftInterval.prescription + rightInterval.prescription) / 2;
  } else {
    // Use right prescription
    prescription = rightInterval.prescription;
  }

  // Create the merged interval
  const mergedInterval = new Interval(leftInterval.min, rightInterval.max, prescription);

  // Replace the 2 indices, starting at the primaryIndex, with the mergedInterval
  // Note: splice mutates the original array and returns the deleted elements
  intervals.splice(left, 2, mergedInterval);

  intervals = intervals.map((interval) => {
    interval.key = UUID.uuidv4(); // Force unique keys for intervals (IE11)
    return interval;
  });

  // Return the (now mutated) array of intervals
  return intervals;
}

export function calculateSplitIntervals(splitIndex, intervals, isSoilLayer, values) {
  let newIntervals = [...intervals];

  const interval = newIntervals[splitIndex];
  const prescription = interval.prescription;

  // Create the split interval
  let leftInterval;
  let rightInterval;
  if (isSoilLayer) {
    const uniqueValues = getIntervalUniqueValues(interval, values);

    leftInterval = new Interval(interval.min, uniqueValues[1], prescription);
    rightInterval = new Interval(uniqueValues[1], interval.max, prescription);
  } else {
    const middle = interval.min + (interval.max - interval.min) / 2;
    leftInterval = new Interval(interval.min, middle, prescription);
    rightInterval = new Interval(middle, interval.max, prescription);
  }

  // Replace the 2 indices, starting at the primaryIndex, with the mergedInterval
  // Note: splice mutates the original array and returns the deleted elements
  newIntervals.splice(splitIndex, 1, leftInterval, rightInterval);
  newIntervals = newIntervals.map((interval) => {
    interval.key = UUID.uuidv4(); // Force unique keys for intervals (IE11)
    return interval;
  });

  // Return the (now mutated) array of intervals
  return newIntervals;
}

function getNextOverrideColor(newAreas) {
  const existingColors = Object.keys(newAreas).map((key) => newAreas[key].color);
  let color = PRESCRIPTION_OVERRIDE_COLORS.find((c) => existingColors.indexOf(c) === -1);

  if (!color) {
    color = tinycolor.random().toHexString();
  }

  return color;
}

export function calculateMaxAllocationPerField(currentMap) {
  const { field, allocationMax } = currentMap;
  const result = field.size * allocationMax;
  return Number(Number(result).toFixed(2));
}

// Don't worry..! The code below is primarily logging for debugging purposes. Read the comments to get a "guided tour".
// bulkOverrides is an optional parameter for bulk prescription mode.
// When provided, it supplies accumulated values (totalPrescription, maxPrescription, and per interval areaFinals)
// so that interval prescriptions are adjusted to make the accumulated bulk total match the accumulated bulk max.
export function calculatePrescriptionLimitAdjustedIntervals(
  prescriptionJob: PrescriptionJob,
  bulkOverrides?: { totalPrescription: number; maxPrescription: number; areaFinals: number[] }
) {
  const { intervals, maxPrescription, totalPrescription, overrideAreas } = prescriptionJob;
  const log = false;

  // Ensure immutability
  const newIntervals = [...intervals];

  let overridesPrescription = 0;

  if (!bulkOverrides && overrideAreas) {
    overridesPrescription = Object.keys(overrideAreas).reduce(
      (acc, cur) => acc + cur * overrideAreas[cur].area,
      0
    );
  }

  // Use accumulated bulk values when provided, otherwise use the job's own values
  const total =
    (bulkOverrides ? bulkOverrides.totalPrescription : totalPrescription) - overridesPrescription;
  const max =
    (bulkOverrides ? bulkOverrides.maxPrescription : maxPrescription) - overridesPrescription;

  // Helper to get areaFinal for an interval at a given index
  const getAreaFinal = (interval, idx) =>
    bulkOverrides ? bulkOverrides.areaFinals[idx] : interval.areaFinal;

  log && console.log('Raw Intervals');
  log &&
    newIntervals.forEach((interval, idx) =>
      console.log('[' + idx + '] Prescription: ' + interval.prescription + ' kg')
    );

  // Calculate the expected ratios (from the user's perspective) according to the adjustment of the Draggable Intervals.
  let expectedRatios;
  if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
    expectedRatios = newIntervals.map(
      (interval, idx) =>
        (interval.prescription * (getAreaFinal(interval, idx) * SQUARE_METER_COVERSION_VALUE)) /
        total
    );
  } else {
    expectedRatios = newIntervals.map(
      (interval, idx) => (interval.prescription * getAreaFinal(interval, idx)) / total
    );
  }
  log && console.log('Expected Ratios');
  log &&
    expectedRatios.forEach((ratio, idx) =>
      console.log('[' + idx + '] Ratio: ' + ratio * 100 + '%')
    );

  // Calculate the expected total ratio (should be very, very close to 100%) - Used for error calculation in the end
  const expectedTotal = expectedRatios.reduce((acc, ratio) => acc + ratio * 100, 0);

  log && console.log('Expected Total: ' + expectedTotal + ' %');

  // Calculate the actual ratios that the re-allocations represent relative to the Prescription Limit (as set in the Settings)
  let actualRatios;
  if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
    actualRatios = newIntervals.map(
      (interval, idx) =>
        (interval.prescription * (getAreaFinal(interval, idx) * SQUARE_METER_COVERSION_VALUE)) / max
    );
  } else {
    actualRatios = newIntervals.map(
      (interval, idx) => (interval.prescription * getAreaFinal(interval, idx)) / max
    );
  }

  log && console.log('Actual Ratios');
  log &&
    actualRatios.forEach((ratio, idx) => console.log('[' + idx + '] Ratio: ' + ratio * 100 + '%'));

  // Calculate the differences between the actual and expected ratios
  const differences = actualRatios.map((ratio, idx) => ratio - expectedRatios[idx]);

  log && console.log('Differences');
  log &&
    differences.forEach((diff, idx) =>
      console.log('[' + idx + '] Difference: ' + diff * 100 + '%')
    );

  // Map the differences to an absolute adjustment for each interval
  const adjustments = differences.map((diff) => {
    // Negative difference means we have to add more to this interval
    // Positive difference means we have to remove from this interval
    // Thus, we invert the difference when talking about adjustments.
    return diff * max * -1;
  });

  log && console.log('Adjustments');
  log &&
    adjustments.forEach((adj, idx) =>
      console.log('[' + idx + '] Adjustment: ' + (adj > 0 ? '+ ' : '- ') + adj + ' kg')
    );

  // Apply the absolute adjustment on each interval
  const adjustedIntervals = newIntervals.map((interval, idx) => {
    const areaFinal = getAreaFinal(interval, idx);
    log && console.log(interval.prescription, adjustments[idx], areaFinal);

    if (areaFinal > 0) {
      if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
        interval.prescription =
          interval.prescription +
          Number(adjustments[idx]) / (areaFinal * SQUARE_METER_COVERSION_VALUE);
      } else {
        interval.prescription = interval.prescription + Number(adjustments[idx]) / areaFinal;
      }
    }

    return interval;
  });

  log && console.log('Adjusted Intervals');
  log &&
    adjustedIntervals.forEach((interval, idx) =>
      console.log('[' + idx + '] Adjusted Prescription: ' + interval.prescription + ' kg')
    );

  // Calculate the adjusted ratios for error calculation
  let adjustedRatios;
  if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
    adjustedRatios = adjustedIntervals.map(
      (interval, idx) =>
        (interval.prescription * (getAreaFinal(interval, idx) * SQUARE_METER_COVERSION_VALUE)) / max
    );
  } else {
    adjustedRatios = adjustedIntervals.map(
      (interval, idx) => (interval.prescription * getAreaFinal(interval, idx)) / max
    );
  }

  log && console.log('Adjusted Ratios');
  log &&
    adjustedRatios.forEach((ratio, idx) =>
      console.log('[' + idx + '] Ratio: ' + ratio * 100 + '%')
    );

  // Calculate the new total sum of ratios for error calculation
  const adjustedTotal = adjustedRatios.reduce((acc, ratio) => acc + ratio * 100, 0);

  log && console.log('Adjusted Total: ' + adjustedTotal + ' %');

  // Calculate the difference between the initial sum of ratios and the final sum of ratios
  const adjustmentError = Number(Number(expectedTotal - adjustedTotal).toFixed(1));

  log && console.log('Adjustment Error: ' + adjustmentError * 100 + ' %');

  return { adjustedIntervals: adjustedIntervals, adjustmentError: adjustmentError };
}

export const getLayerConfig = (layer: FieldLayer, countryCode?: string) => {
  if (!layer) {
    return null;
  }

  if (SURVEY_LAYER_VALUES.includes(layer.toUpperCase())) {
    return getSurveyConfig(layer.toUpperCase(), countryCode);
  }
  if (Object.keys(SATELLITE_LAYERS).includes(layer)) {
    return getSatelliteConfig(layer);
  }
  if (Object.keys(OTHER_LAYERS).includes(layer)) {
    return getOtherLayerConfig(layer);
  }
  if (Object.keys(YIELD_LAYERS).includes(layer.toUpperCase())) {
    return getYieldLayerConfig(layer);
  }
};

export const getBufferPolygons = (field, bufferMeters) => {
  const p = turf.polygon(field.polygon.coordinates);
  const buffered = turf.buffer(p, -bufferMeters / 1000, { units: 'kilometers', steps: 64 });
  const result = [];

  if (buffered && buffered.geometry) {
    buffered.geometry.coordinates.map((entry, idx) => {
      if (Array.isArray(entry[0]) && Array.isArray(entry[0][0])) {
        result.push(...entry);
      } else {
        result.push(entry);
      }
    });
  }

  return result;
};

export const calculateIntervalAreas = (prescriptionJob: PrescriptionJob) => {
  const { values, intervals, fieldSize, overrides, overrideAreas, layerType } = prescriptionJob;
  const hasNullInterval = intervals.some(isNullInterval);
  // Make sure the cells property is reset.
  const newIntervals = [...intervals.map((interval) => ({ ...interval, cells: [] }))];
  // Find out which cells (x-y coordinates in the values array) are covered by which intervals.
  iterateRawValues(values, (x, y, value) => {
    if (
      !(
        value > 0 ||
        (isDemandLayerType(layerType) && value === 0) ||
        (hasNullInterval && value === NULL_INTERVAL_VALUE)
      )
    )
      return;
    const interval = newIntervals.find((item, idx, arr) => {
      if (value === NULL_INTERVAL_VALUE) return isNullInterval(item);
      if (isSoilLayer(prescriptionJob.layer)) return item.min <= value && item.max > value;
      if (value >= item.min && value <= item.max) return true;
      if (idx === arr.length - 1) return true;
    });
    if (interval) {
      interval.cells = interval.cells || [];
      interval.cells.push({ x, y });
    }
  });

  // Summarize the total count of value cells
  const valuesSize =
    prescriptionJob.originalValues !== undefined
      ? prescriptionJob.originalValues.flat().filter((v) => v !== null).length
      : prescriptionJob.values.flat().filter((v) => v !== null).length;
  const intervalSize = newIntervals.reduce((acc, cur) => acc + cur.cells.length, 0);
  // Apparantly there is some 'magic' that works differently for other jobs that LIME. So the total cells are used from values instead of intervals when doing a limejob
  const nullOverrideCount = countOverriddenNullCells(values, overrides);
  const totalCells =
    (prescriptionJob.metaType === MetaTypes.LIME ? valuesSize : intervalSize) + nullOverrideCount;

  // Calculate the areas of each interval based on the covered cells and the total amount of cells.
  // Also calculate the total prescription of each interval based on the interval's final area, where the overridden area has been subtracted.
  newIntervals.forEach((interval) => {
    interval.areaRaw = (interval.cells.length / totalCells) * fieldSize;
    interval.areaOverridden =
      (interval.cells.filter((cell) => {
        const row = overrides[cell.y];
        if (Array.isArray(row)) {
          const override = row[cell.x];
          if (override !== null && override > -1) {
            return true;
          }
        }
        return false;
      }).length /
        totalCells) *
      fieldSize;
    interval.areaFinal = interval.areaRaw - interval.areaOverridden;
    if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
      interval.totalPrescription =
        interval.areaFinal * SQUARE_METER_COVERSION_VALUE * interval.prescription;
    } else {
      interval.totalPrescription = interval.areaFinal * interval.prescription;
    }
  });

  // Save the new intervals
  prescriptionJob.intervals = newIntervals;
  // Summarize the total prescription for the entire field.
  prescriptionJob.totalPrescription = newIntervals.reduce(
    (acc, cur) => acc + cur.totalPrescription,
    0
  );

  // Include overrides in the summation.
  prescriptionJob.totalPrescription += Object.keys(overrideAreas).reduce((acc, key) => {
    const { area, value } = overrideAreas[key];
    if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
      return acc + area * SQUARE_METER_COVERSION_VALUE * value;
    } else {
      return acc + area * value;
    }
  }, 0);
  return prescriptionJob;
};

export const calculateClassificationIntervalAreas = (prescriptionJob: PrescriptionJob) => {
  if (!prescriptionJob.classifications) {
    return prescriptionJob;
  }
  const { intervals, fieldSize, overrides, overrideAreas } = prescriptionJob;

  // Make sure the cells property is reset.
  const newIntervals = [...intervals.map((interval) => ({ ...interval, cells: [] }))];
  const classificationValues = prescriptionJob.classifications['values'];
  const classificationFineValues = prescriptionJob.classifications['mappings']['FINE'];
  // Find out which cells (x-y coordinates in the values array) are covered by which intervals.
  iterateRawValues(classificationValues, (x, y, value) => {
    const interval = newIntervals.find((item, idx, arr) => {
      if (idx === arr.length - 1) return true;
      const classification = Object.keys(classificationFineValues).find((key) =>
        classificationFineValues[key].includes(value)
      );
      return item.classification === classification;
    });

    if (interval) {
      interval.cells = interval.cells || [];
      interval.cells.push({ x, y });
    }
  });

  // Summarize the total count of value cells
  const totalCells = newIntervals.reduce((acc, cur) => acc + cur.cells.length, 0);

  // Calculate the areas of each interval based on the covered cells and the total amount of cells.
  // Also calculate the total prescription of each interval based on the interval's final area, where the overridden area has been subtracted.
  newIntervals.forEach((interval) => {
    interval.areaRaw = (interval.cells.length / totalCells) * fieldSize;
    interval.areaOverridden =
      (interval.cells.filter((cell) => {
        const row = overrides[cell.y];
        if (Array.isArray(row)) {
          const override = row[cell.x];
          if (override !== null && override > -1) {
            return true;
          }
        }
        return false;
      }).length /
        totalCells) *
      fieldSize;
    interval.areaFinal = interval.areaRaw - interval.areaOverridden;
    if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
      interval.totalPrescription =
        interval.areaFinal * SQUARE_METER_COVERSION_VALUE * interval.prescription;
    } else {
      interval.totalPrescription = interval.areaFinal * interval.prescription;
    }
  });

  // Save the new intervals
  prescriptionJob.intervals = newIntervals;
  // Summarize the total prescription for the entire field.
  prescriptionJob.totalPrescription = newIntervals.reduce(
    (acc, cur) => acc + cur.totalPrescription,
    0
  );
  // Include overrides in the summation.
  prescriptionJob.totalPrescription += Object.keys(overrideAreas).reduce((acc, key) => {
    const { area, value } = overrideAreas[key];
    return acc + area * value;
  }, 0);
  return prescriptionJob;
};

export const squareFromLatLng = (latLng, sizeMeters) => {
  const sizeKilometers = sizeMeters / 1000;

  const center = turf.point([latLng.lng, latLng.lat]);
  let nw = turf.transformTranslate(turf.clone(center), sizeKilometers / 2, 0);
  nw = turf.transformTranslate(nw, sizeKilometers / 2, -90);
  const ne = turf.transformTranslate(turf.clone(nw), sizeKilometers, 90);
  const se = turf.transformTranslate(turf.clone(ne), sizeKilometers, 180);
  const sw = turf.transformTranslate(turf.clone(se), sizeKilometers, -90);
  const box = turf.bbox(turf.featureCollection([nw, ne, se, sw]));

  return turf.bboxPolygon(box);
};

export const calculateValueStepSize = (percentage, prescriptionJob) => {
  if (prescriptionJob.unit === PRESCRIPTION_UNIT.PIECES_M2) {
    return (
      (prescriptionJob.maxPrescription /
        (prescriptionJob.fieldSize * SQUARE_METER_COVERSION_VALUE)) *
      percentage
    );
  }
  return (prescriptionJob.maxPrescription / prescriptionJob.fieldSize) * percentage;
};

export const adjustValuesBetweeen = (
  values: number[][],
  newValue: number,
  min: number,
  max: number
): number[][] => {
  const valueMatrix = matrix(values);
  const newValues = map(valueMatrix, (v) => {
    if (v === null) {
      return null;
    }

    if (v < min || v > max) {
      return v;
    }

    return newValue;
  });

  return newValues._data;
};

export const calculateLimeJobIntervals: Interval[] = (
  limePrescriptionValues: number[][],
  merging: bool
) => {
  const highestValue = getMax(limePrescriptionValues.flat());
  const zoneA = createIntervalWithValuesBetween(limePrescriptionValues, 1, 1000);
  const zoneB = createIntervalWithValuesBetween(limePrescriptionValues, 1000.0001, 2000, 2000.0001);
  const zoneC = createIntervalWithValuesBetween(limePrescriptionValues, 2000.0001, 3000, 3000.0001);
  const zoneD = createIntervalWithValuesBetween(limePrescriptionValues, 3000.0001, 4000, 4000.0001);
  const zoneE = createIntervalWithValuesBetween(limePrescriptionValues, 4000.0001, 5000, 5000.0001);
  const zoneF = createIntervalWithValuesBetween(limePrescriptionValues, 5000.0001, 6000, 6000.0001);
  if (highestValue > 6000) {
    const zoneF = createIntervalWithValuesBetween(limePrescriptionValues, 5000.0001, highestValue);
    return [zoneA, zoneB, zoneC, zoneD, zoneE, zoneF];
  }
  return [zoneA, zoneB, zoneC, zoneD, zoneE, zoneF];
};

export const calculateBulkLimeJobIntervals = (
  prescriptionJobs: Record<number, PrescriptionJob>
): Interval[] => {
  const allValues = Object.values(prescriptionJobs)
    .flatMap((job) => {
      return job.values || [];
    })
    .flat()
    .filter((val) => val !== null);

  const highestValue = getMax(allValues);

  const zoneA = createIntervalWithValuesBetween([allValues], 1, 1000);
  const zoneB = createIntervalWithValuesBetween([allValues], 1000.0001, 2000, 2000.0001);
  const zoneC = createIntervalWithValuesBetween([allValues], 2000.0001, 3000, 3000.0001);
  const zoneD = createIntervalWithValuesBetween([allValues], 3000.0001, 4000, 4000.0001);
  const zoneE = createIntervalWithValuesBetween([allValues], 4000.0001, 5000, 5000.0001);
  const zoneF = createIntervalWithValuesBetween([allValues], 5000.0001, 6000, 6000.0001);
  if (highestValue > 6000) {
    const zoneF = createIntervalWithValuesBetween([allValues], 5000.0001, highestValue);
    return [zoneA, zoneB, zoneC, zoneD, zoneE, zoneF];
  }
  return [zoneA, zoneB, zoneC, zoneD, zoneE, zoneF];
};

const createIntervalWithValuesBetween = (
  values: (number | null)[][],
  min: number,
  max: number,
  nextMin: number
): Interval => {
  const valuesBetween = getValuesBetween(values, min, max, nextMin);
  return new Interval(
    min,
    max,
    valuesBetween && valuesBetween.length > 0 ? mean(valuesBetween) : 0
  );
};

const getValuesBetween = (values, min, max, nextMin) => {
  return values.flatMap((row, y) =>
    row.flatMap((val, x) => {
      if (val === null) return [];
      if (val >= min && val <= max) return val;
      if (val > max && val < nextMin) {
        values[y][x] = max;
        return max;
      }
      return [];
    })
  );
};

export const hasRequiredLimeLayers = (fieldSurveys: Survey[]) => {
  const requiredLayers = [SURVEY_LAYERS.RT, SURVEY_LAYERS.HUMUS, SURVEY_LAYERS.CLAY];

  return requiredLayers.every((layer) => fieldSurveys.some((survey) => survey.newImages?.[layer]));
};

export const getBulkBounds = (fields: Field[]) => {
  let north = -Infinity;
  let south = Infinity;
  let east = -Infinity;
  let west = Infinity;

  fields.forEach((field) => {
    north = Math.max(north, field.bounds.north);
    south = Math.min(south, field.bounds.south);
    east = Math.max(east, field.bounds.east);
    west = Math.min(west, field.bounds.west);
  });

  return { north, south, east, west };
};

// Calculates the total area for a given interval across all bulk prescription jobs.
export function getBulkPrescriptionIntervalArea(interval, bulkPrescriptionJobs) {
  if (!bulkPrescriptionJobs || !interval) return 0;
  return Object.values(bulkPrescriptionJobs).reduce((sum, job) => {
    const match = job.intervals?.find((i) =>
      isNullInterval(interval)
        ? isNullInterval(i)
        : !isNullInterval(i) && i.min < interval.max && i.max > interval.min
    );
    return sum + (match?.areaFinal || 0);
  }, 0);
}
